"use client"

import { getBasketSessionState } from "@/actions/selection/client/basket-session-state"
import { sortShippingMethodsByPrice } from "@/lib/utils/checkout/shippingUtils"
import { useSwr } from "@/src/hooks/useSwr"
import { type BasketSessionState } from "@/types/centra/basket-session-state"
import { type CountryWithStates } from "@/types/centra/checkout-api/selection/get-selection"
import { type RequiredTokenAndSelectionMetadata } from "@/types/cookie-types"

/**
 * Single source of truth for the visitor's Centra basket session — both the cookie-level
 * data (preferredApp, metadata, the active-basket gate) AND the live Centra selection.
 *
 * Backed by one route, `/api/centra/basket-session-state`, which returns a `BasketSessionState`
 * discriminated union (`anonymous | preference | active`). The route hits Centra ONLY for the
 * `active` state (valid token); `anonymous` (no cookie) and `preference` (no token / token
 * rejected) are resolved purely from the cookie. The hook therefore always runs — there is no
 * client-side gate — because the no-token short-circuit happens server-side at zero Centra cost.
 *
 * This is the single hook that replaced the earlier two-hook split (a cheap cookie probe
 * plus a separate Centra selection hook, with their separate probe + keep-selection
 * routes), removing the client-side waterfall and the double-mutate between them.
 * One key → one `mutate()` refreshes everything.
 *
 * Revalidation: `revalidateOnFocus` / `revalidateOnReconnect` / `revalidateIfStale` are all on,
 * with SWR's default 2s `dedupingInterval`. The 2s window still collapses the many concurrent
 * consumers (this hook is read in ~30+ components) into a single request on mount, while letting
 * focus / reconnect / navigation actually re-fetch. This is deliberate: a basket WITH products
 * must never go stale (e.g. after another tab or device changes it), so freshness is prioritized.
 * For `active` sessions that means a `selection.keep` round-trip on focus/navigation; `anonymous`
 * and `preference` sessions are resolved from the cookie at zero Centra cost. Focus refetches are
 * capped by SWR's default 5s `focusThrottleInterval`, and `LocaleSyncGuard` additionally calls
 * `mutate()` on cross-tab `basket_session` cookie changes for instant sync.
 */
export const useSWRBasketSessionState = () => {
    const { data, error, isLoading, isValidating, mutate } = useSwr<BasketSessionState>(
        "/centra/basket-session-state",
        {
            // POST + credentials (the cookie is the whole point) — see the client action.
            fetcher: getBasketSessionState,
            config: {
                revalidateOnFocus: true,
                revalidateOnReconnect: true,
                revalidateIfStale: true,
                errorRetryCount: 3,
                errorRetryInterval: 3000,
                keepPreviousData: true
            }
        }
    )

    // Narrow to the active state once so selection-derived fields are type-safe.
    const activeState = data?.status === "active" ? data : null

    const checkoutApiSelection = activeState?.checkoutApiSelection ?? null
    const selection = checkoutApiSelection?.selection ?? null
    const location = checkoutApiSelection?.location ?? null
    const countriesWithStates = (checkoutApiSelection?.countries ??
        []) as CountryWithStates[]
    const shippingMethods = sortShippingMethodsByPrice(
        checkoutApiSelection?.shippingMethods ?? []
    )

    // Re-expose the active session's cookie data in the legacy `extractedBasketSessionCookie`
    // shape so existing consumers (e.g. UserPropertiesInitializer) keep working unchanged.
    const extractedBasketSessionCookie: RequiredTokenAndSelectionMetadata | null =
        activeState
            ? {
                  preferredApp: activeState.preferredApp,
                  token: activeState.token,
                  metadata: activeState.metadata
              }
            : null

    return {
        // Session / gate
        status: data?.status ?? "anonymous",
        hasActiveBasket: activeState !== null,
        preferredApp: data?.preferredApp ?? null,
        // Cookie metadata (market/language/country/currency) — present for `preference`
        // and `active`, null when anonymous. Carries the user's chosen country even on
        // language-only locales (e.g. `/en`) where the path doesn't encode it.
        metadata: data?.metadata ?? null,

        // Selection data
        selection,
        location,
        countriesWithStates,
        shippingMethods,
        checkoutApiSelection,
        extractedBasketSessionCookie,

        // SWR meta
        error,
        isError: !!error,
        isLoadingSelection: isLoading,
        isValidating,
        mutate
    }
}

export default useSWRBasketSessionState
